/**
 * LocaisContagemTab - Tab for counting locations management
 */
import React, { useMemo, useState } from 'react';
import { Plus, Upload, FileSpreadsheet, Pencil, Trash2, Info, Building2 } from 'lucide-react';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import { ActionIcon, Group } from '@mantine/core';
import type { LocalContagem } from '../../hooks/useHospitalData';
import { MRT_LOCALIZATION_PT_BR } from './tableLocalization';
import { LocaisInfoModal } from './LocaisInfoModal';
import PermissionGate, { usePermissoesTela } from '../PermissionGate';

interface LocaisContagemTabProps {
  locaisContagem: LocalContagem[];
  onAddLocal: () => void;
  onEditLocal: (local: LocalContagem) => void;
  onDeleteLocal: (localId: number) => void;
  onImportLocais: () => void;
  onBulkImportLocais: () => void;
  onExportLocais: () => void;
}

export const LocaisContagemTab: React.FC<LocaisContagemTabProps> = ({
  locaisContagem,
  onAddLocal,
  onEditLocal,
  onDeleteLocal,
  onImportLocais,
  onBulkImportLocais,
  onExportLocais,
}) => {
  const [showInfoModal, setShowInfoModal] = useState(false);
  const { podeCriar, podeEditar, podeExcluir, podeExportar } = usePermissoesTela('HOSPITAIS_LIST');

  // Columns definition
  const columns = useMemo<MRT_ColumnDef<LocalContagem>[]>(
    () => [
      {
        accessorKey: 'local',
        header: 'Local da Contagem',
      },
      {
        accessorKey: 'responsavel',
        header: 'Responsável',
      },
      {
        accessorKey: 'telefone',
        header: 'Telefone',
        accessorFn: (row) => row.telefone || '-',
      },
      {
        accessorKey: 'ramal',
        header: 'Ramal',
        accessorFn: (row) => row.ramal || '-',
      },
      {
        accessorKey: 'email',
        header: 'Email',
        accessorFn: (row) => row.email || '-',
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: locaisContagem,
    enableColumnFilters: true,
    enableSorting: true,
    enablePagination: true,
    enableRowActions: true,
    positionActionsColumn: 'last',
    enableDensityToggle: false,
    initialState: {
      showColumnFilters: true,
      density: 'xs',
    },
    mantineTableProps: {
      highlightOnHover: true,
      striped: true,
      withTableBorder: true,
      withColumnBorders: true,
      sx: {
        'th': {
          padding: '8px 12px !important',
        },
        'td': {
          padding: '4px 12px !important',
        }
      }
    },
    renderRowActions: ({ row }) => (
      <Group gap={4} wrap="nowrap">
        {podeEditar && (
          <ActionIcon
            onClick={() => onEditLocal(row.original)}
            variant="subtle"
            color="gray"
            size="sm"
            title="Editar"
          >
            <Pencil size={14} />
          </ActionIcon>
        )}
        {podeExcluir && (
          <ActionIcon
            onClick={() => onDeleteLocal(row.original.id)}
            variant="subtle"
            color="red"
            size="sm"
            title="Deletar"
          >
            <Trash2 size={14} />
          </ActionIcon>
        )}
      </Group>
    ),
    mantinePaperProps: {
      shadow: 'none',
      withBorder: true,
      radius: 'md',
    },
    localization: MRT_LOCALIZATION_PT_BR,
  });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Locais de Contagem</h2>
        <div className="flex items-center gap-2">
          <PermissionGate tela="HOSPITAIS_LIST" acao="criar">
            <button
              onClick={onAddLocal}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              <span>Adicionar</span>
            </button>
          </PermissionGate>
          <PermissionGate tela="HOSPITAIS_LIST" acao="editar">
            <button
              onClick={onImportLocais}
              className="px-3 py-1.5 text-sm border border-green-500 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600 dark:text-green-400 flex items-center gap-1.5"
            >
              <Upload className="w-4 h-4" />
              <span>Importar</span>
            </button>
          </PermissionGate>
          <PermissionGate tela="HOSPITAIS_LIST" acao="editar">
            <button
              onClick={onBulkImportLocais}
              className="px-3 py-1.5 text-sm border border-purple-500 rounded-md hover:bg-purple-50 dark:hover:bg-purple-900/20 text-purple-600 dark:text-purple-400 flex items-center gap-1.5"
              title="Importar locais para múltiplos hospitais via CNPJ"
            >
              <Building2 className="w-4 h-4" />
              <span>Upload em Lote</span>
            </button>
          </PermissionGate>
          <button
            onClick={() => setShowInfoModal(true)}
            className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition"
            title="Informações sobre importação"
          >
            <Info className="w-4 h-4" />
          </button>
          <PermissionGate tela="HOSPITAIS_LIST" acao="exportar">
            <button
              onClick={onExportLocais}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-1.5"
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span>Exportar</span>
            </button>
          </PermissionGate>
        </div>
      </div>
      <div className="p-0">
        <MantineReactTable table={table} />
      </div>
      
      {showInfoModal && (
        <LocaisInfoModal onClose={() => setShowInfoModal(false)} />
      )}
    </div>
  );
};
